<template>
  <div class="calendar-range">
    <div class="months-list">
      <CalendarMonth
          v-for="month in monthList"
          :key="month"
          :month-date="month"
          :from="selected.from.unix"
          :to="selected.to.unix"
          :price="props.minPrice"
          @date-click="onDateClick"
      />
    </div>
    <hr class="mt-6 mb-6">
    <div class="bottom-block">
      <div class="pb-6">
        <span class="text-body-1">{{bottomText}} <span class="fw-medium">({{datesCount}} {{$t('nights')}})</span></span>
      </div>
    </div>
    <button class="btn btn-tertiary btn-icon btn-size-l button-previous" @click="changeMonth(-1)">
      <span class="icon icon-chevron-left color-dark-grey"/>
    </button>
    <button class="btn btn-tertiary btn-icon btn-size-l button-next" @click="changeMonth(1)">
      <span class="icon icon-chevron-right color-dark-grey"/>
    </button>
  </div>
</template>

<script setup lang="ts">
import dayjs from '~/utils/dayjs'
import {useSearchStore} from "~/store/search";
const { t } = useI18n()

const searchStore = useSearchStore()
const props = defineProps({minPrice: {type: String, default: ''}})
const emit = defineEmits(['dateChanged', 'close'])
const monthList = ref([] as any[])
const selected = ref({from: {value: '', unix: 0}, to: {value: '', unix: 0}})
const datesCount = ref(0)
const bottomText = ref('' as string)
const monthCount = 2
let startDate: any = null
let monthModifier = 0

function makeMonthsList() {
  monthList.value = []
  for (let i = 0; i < monthCount; i++) {
    monthList.value.push(startDate.add(monthModifier + i, 'month').format('YYYY-MM-DD'))
  }
}

function onDateClick(date: any) {
  const from = selected.value.from
  const to = selected.value.to
  if (!from.value) {
    from.value = date.value
    from.unix = date.unix
  } else if (from.value && !to.value) {
    if (from.value === date.value) {
      from.value = ''
      from.unix = 0
    } else {
      to.value = date.value
      to.unix = date.unix
    }
  } else if (from.value && to.value) {
    from.value = date.value
    from.unix = date.unix
    to.value = ''
    to.unix = 0
  }
  if (from.unix && to.unix && (from.unix > to.unix)) {
    const tempValue = from.value
    const tempUnix = from.unix
    from.value = to.value
    from.unix = to.unix
    to.value = tempValue
    to.unix = tempUnix
  }
  checkDatesCount()
  emit('dateChanged', [from.value || null, to.value || null])
}

function getDates() {
  if (searchStore.dates[0]) {
    selected.value.from.value = searchStore.dates[0]
    selected.value.from.unix = dayjs(searchStore.dates[0]).unix()
  }
  if (searchStore.dates[1]) {
    selected.value.to.value = searchStore.dates[1]
    selected.value.to.unix = dayjs(searchStore.dates[1]).unix()
  }
}

function checkDatesCount() {
  const from = selected.value.from
  const to = selected.value.to
  if (from.value && to.value) {
    datesCount.value = Math.abs(dayjs(from.value).diff(dayjs(to.value), 'days'))
    bottomText.value = `${dayjs(from.value).format('ddd, DD MMM')} — ${dayjs(to.value).format('ddd, DD MMM')}`
  } else {
    datesCount.value = 0
    if (from.value) {
      bottomText.value = `${dayjs(from.value).format('ddd, DD MMM')} — `
    } else {
      bottomText.value = t('Select a check-in date')
    }
  }
}

function changeMonth(diff: number) {
  monthModifier += diff
  makeMonthsList()
}

function checkFirstMonth() {
  const currentMonth = dayjs().startOf('month')
  if (selected.value.from?.value) {
    const suggestedMonthFrom = dayjs(selected.value.from?.value).startOf('month')
    const suggestedMonthTo = dayjs(selected.value.to?.value).startOf('month')
    if (suggestedMonthFrom.diff(suggestedMonthTo, 'month') === 0) {
      const diff = suggestedMonthFrom.diff(currentMonth, 'month')
      startDate = diff > 0 ? suggestedMonthFrom.subtract(1, 'month') : currentMonth
    } else {
      startDate = suggestedMonthFrom
    }
  } else {
    startDate = currentMonth
  }
}

getDates()
checkFirstMonth()
makeMonthsList()

onMounted(() => {
  checkDatesCount()
})

</script>

<style lang="scss" scoped>
.calendar-range {
  padding: 16px 16px 0;
  position: relative;
  .months-list {
    display: flex;
    gap: 24px;
  }
  .bottom-block {
    text-align: center;
    button {
      width: 100%;
    }
  }
  .button-previous {
    position: absolute;
    top: 16px;
    left: 16px;
  }
  .button-next {
    position: absolute;
    top: 16px;
    right: 16px;
  }
}
</style>
